import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, AlertTriangle, Check, Copy, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteLayout from "@/components/SiteLayout";

const criticita = [
  { p: "Smaltimento macerie non specificato", w: "Può diventare un costo aggiuntivo dopo l’inizio dei lavori.", a: "Chiedi se è già incluso nel prezzo." },
  { p: "Voce impianto elettrico troppo generica", w: "Non sono indicate quantità né numero di punti.", a: "Fatti indicare numero di punti e materiali." },
  { p: "Prezzo da verificare", w: "La voce risulta superiore ai riferimenti disponibili.", a: "Chiedi il dettaglio di quantità, materiali e manodopera." },
];

const primaDiFirmare = [
  "L’IVA è inclusa o esclusa?",
  "Lo smaltimento delle macerie è compreso?",
  "Le voci “a corpo” a quali quantità si riferiscono?",
  "I ripristini dopo gli impianti sono inclusi?",
  "Cosa succede in caso di ritardi?",
];

const messaggio = "Buongiorno, prima di procedere vorrei alcuni chiarimenti sul preventivo:\n1. Gli importi sono IVA inclusa o esclusa?\n2. Lo smaltimento delle macerie è compreso?\n3. Le voci “a corpo” a quali quantità si riferiscono?\nGrazie.";

export default function EsempioReport() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const start = () => navigate("/controlla-preventivo");
  const copy = () => { navigator.clipboard.writeText(messaggio); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <SiteLayout
      seo={{ title: "Esempio di report", description: "Ecco cosa ricevi con il report completo OKKIO: costi mancanti, voci poco chiare, prezzi da verificare e possibili extracosti. Esempio dimostrativo.", path: "/esempio-report" }}
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Esempio report", to: "/esempio-report" }]}
      narrow>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] leading-[1.12]">Ecco cosa ricevi con il report completo.</h1>
      <p className="mt-3 text-lg text-[#334155] leading-relaxed">Un controllo chiaro di costi mancanti, voci poco chiare, prezzi da verificare e possibili extracosti.</p>

      {/* Esempio risultato */}
      <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="example-result">
        <div className="flex items-end gap-3">
          <span className="font-heading text-5xl font-semibold text-[#0F172A] leading-none">61</span>
          <span className="text-[#64748B] mb-1.5">/ 100</span>
          <span className="ml-auto inline-flex items-center rounded-full bg-[#3157F6]/10 text-[#3157F6] px-2.5 py-1 text-xs font-semibold">Esempio dimostrativo</span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <p className="text-[#334155] font-medium">Preventivo da chiarire prima di firmare</p>
          <span className="inline-flex items-center rounded-full bg-[#F59E0B]/10 text-[#B45309] px-2 py-0.5 text-xs font-semibold">Rischio medio</span>
        </div>
        <div className="mt-5 space-y-2.5">
          {[["5", "punti da controllare"], ["3", "possibili costi mancanti"], ["2", "prezzi da verificare"]].map(([n, l]) => (
            <div key={l} className="flex items-center gap-3">
              <span className="font-heading text-xl font-semibold text-[#0F172A] w-8">{n}</span>
              <span className="text-[#334155]">{l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Criticità esempio */}
      <h2 className="mt-10 font-heading text-2xl font-semibold text-[#0F172A]">Esempi di punti da controllare</h2>
      <div className="mt-4 space-y-3">
        {criticita.map((c, i) => (
          <div key={c.p} className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid={`example-crit-${i}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#DC3F4C] flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="font-heading font-semibold text-[#0F172A]">{c.p}</p>
                <p className="mt-1 text-sm text-[#64748B]"><b className="text-[#334155]">Perché conta:</b> {c.w}</p>
                <p className="mt-1 text-sm text-[#3157F6] font-semibold">Cosa chiedere: {c.a}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Prima di firmare */}
      <div className="mt-8 rounded-2xl border-2 border-[#3157F6]/25 bg-[#3157F6]/[0.04] p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-[#3157F6] flex items-center justify-center"><ClipboardCheck className="w-5 h-5 text-white" strokeWidth={2} /></span>
          <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Prima di firmare, chiarisci</h2>
        </div>
        <ul className="space-y-3">
          {primaDiFirmare.map((it) => (
            <li key={it} className="flex items-start gap-3 text-[#0F172A]"><Check className="w-5 h-5 text-[#3157F6] flex-shrink-0 mt-0.5" strokeWidth={2.4} /> {it}</li>
          ))}
        </ul>
      </div>

      {/* Messaggio pronto */}
      <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6">
        <h2 className="font-heading text-lg font-semibold text-[#0F172A]">Messaggio pronto per l’impresa</h2>
        <div className="mt-3 rounded-xl bg-[#F7F9FC] border border-[#E2E8F0] p-4 text-sm text-[#334155] whitespace-pre-wrap leading-relaxed">{messaggio}</div>
        <Button onClick={copy} data-testid="example-copy"
          className="mt-4 rounded-xl h-12 px-5 font-semibold bg-[#3157F6] hover:bg-[#2444D8] text-white">
          {copied ? <><Check className="w-5 h-5 mr-1.5" /> Copiato</> : <><Copy className="w-5 h-5 mr-1.5" /> Copia il messaggio</>}
        </Button>
      </div>

      {/* CTA finale */}
      <div className="mt-10 rounded-3xl bg-[#3157F6] text-white p-7 text-center">
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Controlla ora il tuo preventivo</h2>
        <Button onClick={start} data-testid="er-cta"
          className="mt-5 w-full rounded-xl h-14 px-6 text-base font-semibold bg-white text-[#3157F6] hover:bg-[#EAEFFE]">
          Controlla il mio preventivo <ArrowRight className="w-5 h-5 ml-1" />
        </Button>
        <p className="mt-3 text-white/85">Anteprima gratuita • Report completo €9,99</p>
      </div>
    </SiteLayout>
  );
}
